import { useState, useRef, useEffect } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import VideoOffIcon from "../../assets/icons/video-off.svg";
import VideoIcon from "../../assets/icons/video.svg";
import TranscriptIcon from "../../assets/icons/transcript.svg";
import HelpIcon from "../../assets/icons/help-circle.svg";
import ResourcesIcon from "../../assets/icons/file-06.svg";
import { ThumbUpAltOutlined, CloseRounded } from "@mui/icons-material";
import Cookies from "js-cookie";

import HelpTooltip from "../../components/CallScreen/HelpTooltip";

import { detectMediaAccess } from "../../utils";

import theme from "../../Theme";
import DefaultButton from "./DefaultButton";

const CallToolbar = ({
  videoEnabled,
  handleToggleVideo,
  handleEndSimulation,
  openHelpModal,
  panelValue,
  showSidePanel,
  setPanelValue,
  openSidePanel,
  scenarioData,
  isExternalMode,
  isMobile,
  isSpeaking,
  onHelpTooltipClose,
  isFeedbackSession,
  handleToggleFullscreen,
  isFullscreen,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isInteracting, setIsInteracting] = useState(false);
  const toolbarRef = useRef(null);
  const timeoutRef = useRef(null);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [hasMediaAccess, setHasMediaAccess] = useState({});
  const [hasCompletedSim, setHasCompletedSim] = useState(false);
  const [, setTick] = useState(0);

  const forceUpdate = () => {
    setTick((t) => t + 1); // just bump a counter to trigger render
  };

  useEffect(() => {
    if (isSpeaking && !isInteracting) {
      setIsVisible(false);
    } else if (!isSpeaking) {
      setIsVisible(true);
    }
  }, [isSpeaking, isInteracting]);

  useEffect(() => {
    if (!Cookies.get("GN-CompletedSim")) {
      setShowHelpTooltip(true);
      const isSecure = window.location.protocol === "https:";
      Cookies.set("GN-CompletedSim", "false", {
        expires: 365,
        secure: isSecure, // only force secure on HTTPS
        sameSite: isSecure ? "None" : "Lax", // cross-site safe if secure, fallback for dev
      });
    }

    (async () => {
      const access = await detectMediaAccess();
      setHasMediaAccess(access);
    })();

    const handleClickOutside = (event) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target)) {
        setIsVisible(true);
      }
    };

    const handleTouchOutside = (event) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target)) {
        setIsVisible(true);
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("touchstart", handleTouchOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("touchstart", handleTouchOutside);
    };
  }, []);

  useEffect(() => {
    let lastAccess = {};
    let lastCookie = Cookies.get("GN-CompletedSim");

    const check = async () => {
      const access = await detectMediaAccess();
      const cookieValue = Cookies.get("GN-CompletedSim");

      const changed =
        access.camera !== lastAccess.camera ||
        access.microphone !== lastAccess.microphone ||
        cookieValue !== lastCookie;

      if (changed) {
        setHasMediaAccess(access);
        setHasCompletedSim(cookieValue);
        forceUpdate();
        lastAccess = access;
        lastCookie = cookieValue;
      }
    };

    const interval = setInterval(check, 3000);
    check();

    return () => clearInterval(interval);
  }, []);

  // Handle toolbar interaction to prevent fade-out
  const handleInteractionStart = () => {
    setIsInteracting(true);
    setIsVisible(true);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout to reset interaction state
    timeoutRef.current = setTimeout(() => {
      setIsInteracting(false);
    }, 2000); // 2 seconds delay before allowing fade-out
  };

  const handleInteractionEnd = () => {
    // Start the timeout when interaction ends
    timeoutRef.current = setTimeout(() => {
      setIsInteracting(false);
    }, 2000);
  };

  const handleOpenTab = (value) => {
    setPanelValue(value);
    openSidePanel();
  };

  const getIconButtonStyle = (iconTabIndex) => {
    const isActive = panelValue === iconTabIndex && showSidePanel;
    return {
      minWidth: "none",
      color: isActive
        ? theme.palette.text.button_hover
        : theme.palette.border.primary,
      backgroundColor: isActive
        ? theme.palette.background.primary
        : "transparent",
      "&:hover, &.tooltip-active": {
        backgroundColor: theme.palette.background.primary,
        color: theme.palette.text.button_hover,
      },
      [theme.breakpoints.down("md")]: {
        backgroundColor: theme.palette.background.primary,
        padding: "14px",
      },
    };
  };

  const recommendationsHidden =
    isExternalMode || !scenarioData?.has_scoring_engine;
  const resourcesExist = scenarioData?.resources !== null;

  return (
    <Box
      id="call-toolbar"
      ref={toolbarRef}
      onMouseEnter={handleInteractionStart}
      onMouseLeave={handleInteractionEnd}
      onTouchStart={handleInteractionStart}
      onTouchEnd={handleInteractionEnd}
      sx={{
        flexGrow: 1,
        maxWidth: "419px",
        height: "80px",
        borderRadius: 999,
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: `0 ${theme.containers.paddingDesktop}`,
        pointerEvents: isVisible ? "auto" : "none",
        transition: "opacity 0.3s",
        opacity: isVisible ? 1 : 0,
        [theme.breakpoints.down("md")]: {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          borderRadius: "12px",
        },
        "& img": {
          [theme.breakpoints.down("md")]: {
            width: "20px",
            height: "20px",
          },
        },
      }}
    >
      <Tooltip title="Video ON/OFF" placement="top" arrow>
        <IconButton onClick={handleToggleVideo} sx={getIconButtonStyle(false)}>
          {videoEnabled ? (
            <img src={VideoIcon} alt="" />
          ) : (
            <img src={VideoOffIcon} alt="" />
          )}
        </IconButton>
      </Tooltip>

      {isFeedbackSession && (
        <Tooltip title="Toggle Fullscreen" placement="top" arrow>
          <IconButton
            sx={getIconButtonStyle(false)}
            onClick={handleToggleFullscreen}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.6667 8.33333L17.5 2.5M17.5 2.5H12.5M17.5 2.5V7.5M8.33333 11.6667L2.5 17.5M2.5 17.5H7.5M2.5 17.5L2.5 12.5"
                stroke="#D5D7DA"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </IconButton>
        </Tooltip>
      )}

      {!isFeedbackSession && (
        <>
          <Tooltip title="Transcript" placement="top" arrow>
            <IconButton
              onClick={() => handleOpenTab(0)}
              sx={getIconButtonStyle(0)}
            >
              <img src={TranscriptIcon} alt="" />
            </IconButton>
          </Tooltip>

          {!recommendationsHidden ? (
            <Tooltip title="Recommendations" placement="top" arrow>
              <IconButton
                onClick={() => handleOpenTab(1)}
                sx={getIconButtonStyle(1)}
              >
                <ThumbUpAltOutlined />
              </IconButton>
            </Tooltip>
          ) : null}

          {resourcesExist ? (
            <Tooltip title="Resources" placement="top" arrow>
              <IconButton
                onClick={() => handleOpenTab(1)}
                sx={getIconButtonStyle(1)}
              >
                <img src={ResourcesIcon} alt="" />
              </IconButton>
            </Tooltip>
          ) : null}

          <Tooltip
            open={showHelpTooltip}
            title={
              <HelpTooltip
                isNewUser={hasCompletedSim}
                mediaAccess={hasMediaAccess}
                handleClose={() => {
                  setShowHelpTooltip(false);
                  onHelpTooltipClose();
                }}
              />
            }
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: "none",
                  color: "#030039",
                  background: "#030039",
                  borderRadius: "8px",
                  "& span:before": {
                    background: "#030039",
                  },
                },
              },
            }}
            placement="top"
          >
            <IconButton
              onClick={() => setShowHelpTooltip(!showHelpTooltip)}
              sx={getIconButtonStyle(false)}
              className={showHelpTooltip ? "tooltip-active" : null}
            >
              <img src={HelpIcon} alt="" />
            </IconButton>
          </Tooltip>
        </>
      )}
      <DefaultButton
        variant="contained"
        textColor={"#fff"}
        startIcon={<CloseRounded />}
        borderColor={"transparent"}
        onClick={handleEndSimulation}
        sx={{
          color: "#fff",
          backgroundColor: "#d82c21",
          "&:hover": {
            backgroundColor: "#b71c1c",
          },
        }}
      >
        {!isMobile ? "End Simulation" : " "}
      </DefaultButton>
    </Box>
  );
};

export default CallToolbar;
